import { ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useId, useState } from 'react';
import { SECTIONS } from 'src/data/sections.ts';
import { EASE_STANDARD } from 'src/lib/motion.ts';
import { isAnswered, scalePct } from 'src/lib/scoring.ts';
import { Answers, Question, Section, Subgroup } from 'src/types/types.ts';

const SCALE_TICKS = [1, 2, 3, 4, 5];

export function QuestionBar({ value }: { value: number | undefined }) {
  // „Keine Angabe" (SKIP_VALUE) und unbeantwortete Fragen werden gleich behandelt:
  // explizit als „keine Angabe", nie als Skalenwert 0.
  if (!isAnswered(value)) {
    return (
      <div className="flex items-center gap-3">
        <div
          className="h-1.5 flex-1 rounded-full border border-zeb-muted"
          style={{
            backgroundImage:
              'repeating-linear-gradient(45deg, var(--zeb-gray-100) 0px, var(--zeb-gray-100) 6px, transparent 6px, transparent 12px)'
          }}
        />
        <span className="w-20 shrink-0 text-right text-meta text-zeb-gray-500">keine Angabe</span>
      </div>
    );
  }

  const pct = scalePct(value);

  return (
    <div className="flex items-center gap-3">
      <div className="flex-1">
        <div className="relative h-1.5 rounded-full bg-zeb-muted">
          <div className="h-full rounded-full bg-zeb-primary" style={{ width: `${pct}%` }} />
          <div
            className="absolute top-1/2 h-3.5 w-3.5 rounded-full border-[3px] border-zeb-primary bg-white"
            style={{ left: `${pct}%`, transform: 'translate(-50%, -50%)' }}
          />
        </div>
        <div data-scale-ticks className="relative mt-1.5 h-3 text-micro text-zeb-gray-400">
          {SCALE_TICKS.map((n) => (
            <span key={n} className="absolute -translate-x-1/2" style={{ left: `${scalePct(n)}%` }}>
              {n}
            </span>
          ))}
        </div>
      </div>
      <span className="w-20 shrink-0 text-right text-meta tabular-nums text-zeb-tertiary">
        {value.toFixed(1).replace('.', ',')}
      </span>
    </div>
  );
}

function QuestionRow({
  question,
  value,
  tinted
}: {
  question: Question;
  value: number | undefined;
  tinted: boolean;
}) {
  return (
    <div data-question-row className={`px-5 py-3.5 ${tinted ? 'bg-zeb-info-surface' : 'bg-white'}`}>
      <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:gap-6">
        <p className="text-body-sm text-zeb-tertiary lg:flex-1">{question.text}</p>
        <div className="lg:w-[380px] lg:shrink-0">
          <QuestionBar value={value} />
        </div>
      </div>
    </div>
  );
}

function SubgroupBlock({
  subgroup,
  answers,
  startIndex
}: {
  subgroup: Subgroup;
  answers: Answers;
  startIndex: number;
}) {
  return (
    <div>
      {subgroup.title && (
        <div className="px-5 pb-1 pt-4 text-note font-bold text-zeb-primary">{subgroup.title}</div>
      )}
      {subgroup.questions.map((q, i) => (
        <QuestionRow
          key={q.id}
          question={q}
          value={answers[q.id]}
          tinted={(startIndex + i) % 2 === 1}
        />
      ))}
    </div>
  );
}

export function SectionAccordion({
  section,
  answers,
  score
}: {
  section: Section;
  answers: Answers;
  score?: number;
}) {
  const [open, setOpen] = useState(false);
  const baseId = useId();
  const panelId = `${baseId}-panel`;
  const headerId = `${baseId}-header`;
  const Icon = section.icon;

  // Running index across all subgroups so the row background rhythm continues
  // uninterrupted through the whole section, instead of resetting per subgroup.
  const subgroupsWithOffset = section.subgroups.reduce<
    { subgroup: Subgroup; startIndex: number }[]
  >((acc, subgroup) => {
    const prev = acc[acc.length - 1];
    const startIndex = prev ? prev.startIndex + prev.subgroup.questions.length : 0;
    acc.push({ subgroup, startIndex });
    return acc;
  }, []);

  return (
    <div className="overflow-hidden rounded-lg border border-zeb-muted bg-white shadow-zeb-xs">
      <button
        id={headerId}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-center gap-3 px-5 py-4 text-left">
        <Icon className="h-5 w-5 shrink-0 text-zeb-primary" aria-hidden="true" />
        <span
          className="flex-1 text-body-lg text-zeb-tertiary"
          style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, letterSpacing: '-0.02em' }}>
          {section.title}
        </span>
        {/* Sektionen ohne substanzielle Antworten (alles „Keine Angabe") zeigen
            explizit „keine Angabe" — nie den unmöglichen Skalenwert „0,0 / 5,0". */}
        <span className="shrink-0 text-sm tabular-nums text-zeb-gray-500">
          {score !== undefined ? `${score.toFixed(1).replace('.', ',')} / 5,0` : 'keine Angabe'}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-zeb-gray-400 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="panel"
            id={panelId}
            role="region"
            aria-labelledby={headerId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: EASE_STANDARD }}
            className="overflow-hidden border-t border-zeb-muted">
            {subgroupsWithOffset.map(({ subgroup, startIndex }) => (
              <SubgroupBlock
                key={subgroup.id}
                subgroup={subgroup}
                answers={answers}
                startIndex={startIndex}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function QuestionBreakdownSection({
  answers,
  scores = {}
}: {
  answers: Answers;
  scores?: Record<string, number | undefined>;
}) {
  return (
    <div className="space-y-4">
      {SECTIONS.map((section) => (
        <SectionAccordion
          key={section.id}
          section={section}
          answers={answers}
          score={scores[section.id]}
        />
      ))}
    </div>
  );
}
